import { useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Loader2,
  Sparkles,
  Calendar,
  Hash,
  Lock,
  Save,
  Copy,
  Check,
  Share2,
  Star,
  MessageCircle,
  Phone,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { soundManager } from "@/lib/soundManager";
import Navigation from "@/components/Navigation";
import { PredictionCard } from "@/components/PredictionCard";

type SystemType = "freely" | "by_date" | "by_number";

export default function PredictNew() {
  const { isAuthenticated } = useAuth();
  const [selectedSystem, setSelectedSystem] = useState<SystemType | null>(null);
  const [digitType, setDigitType] = useState<number>(4);
  const [count, setCount] = useState<number>(10);
  const [dateInput, setDateInput] = useState<string>("");
  const [numberInput, setNumberInput] = useState<string>("");
  const [predictions, setPredictions] = useState<any[]>([]);
  const [dateCombinations, setDateCombinations] = useState<any[] | null>(null);
  const [predictionId, setPredictionId] = useState<number | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [copiedDateCombos, setCopiedDateCombos] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  // Tonight's Best state
  const [tonightPredictions, setTonightPredictions] = useState<any[]>([]);
  const [tonightDate, setTonightDate] = useState<string>("");
  const [showTonightResults, setShowTonightResults] = useState(false);
  const [tonightCopiedAll, setTonightCopiedAll] = useState(false);
  const [showTonightShareDialog, setShowTonightShareDialog] = useState(false);
  const [tonightShareCopied, setTonightShareCopied] = useState(false);

  // Helper: format date nicely
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "tonight";
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  };

  // Build share text
  const buildShareText = (nums: any[], dateStr: string) => {
    const numbersStr = nums.map((p) => p.number).join("-");
    const dateLabel = formatDate(dateStr);
    return `🌟 Wega di Number AI Predictions for ${dateLabel} (9 PM draw):\n${numbersStr}\n\nGenerated by Wega di Number AI Predictor`;
  };

  const handleCopyAll = () => {
    const allNumbers = predictions.map((p) => p.number).join("-");
    navigator.clipboard.writeText(allNumbers);
    setCopiedAll(true);
    toast.success("All predictions copied!");
    setTimeout(() => setCopiedAll(false), 2000);
  };

  const handleShareCopy = (nums: any[], dateStr: string) => {
    const text = buildShareText(nums, dateStr);
    navigator.clipboard.writeText(text);
    setShareCopied(true);
    toast.success("Share text copied!");
    setTimeout(() => setShareCopied(false), 2000);
  };

  const handleTonightShareCopy = () => {
    const text = buildShareText(tonightPredictions, tonightDate);
    navigator.clipboard.writeText(text);
    setTonightShareCopied(true);
    toast.success("Share text copied!");
    setTimeout(() => setTonightShareCopied(false), 2000);
  };

  const handleWhatsApp = (nums: any[], dateStr: string) => {
    const text = encodeURIComponent(buildShareText(nums, dateStr));
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const handleSMS = (nums: any[], dateStr: string) => {
    const text = encodeURIComponent(buildShareText(nums, dateStr));
    window.open(`sms:?body=${text}`, "_blank");
  };

  const generateMutation = trpc.prediction.generateBySystem.useMutation({
    onSuccess: (data) => {
      soundManager.playSuccess();
      setPredictions(data.predictions);
      setDateCombinations(data.dateCombinations ?? null);
      setPredictionId(data.predictionId);
      toast.success(`Generated ${data.predictions.length} predictions!`);
    },
    onError: (error) => {
      soundManager.playError();
      toast.error(error.message || "Failed to generate predictions");
    },
  });

  const tonightsBestMutation = trpc.prediction.tonightsBest.useMutation({
    onSuccess: (data) => {
      soundManager.playSuccess();
      setTonightPredictions(data.predictions);
      setTonightDate(data.targetDate);
      setShowTonightResults(true);
      toast.success(`Tonight's best 5 numbers generated!`);
    },
    onError: (error) => {
      soundManager.playError();
      toast.error(error.message || "Failed to generate tonight's best numbers");
    },
  });

  const saveForAnalysisMutation = trpc.prediction.saveForAnalysis.useMutation({
    onSuccess: () => {
      soundManager.playSuccess();
      toast.success("Predictions saved for AI analysis!");
    },
    onError: (error) => {
      soundManager.playError();
      toast.error(error.message || "Failed to save for analysis");
    },
  });

  const handleCopyDateCombinations = () => {
    if (!dateCombinations || dateCombinations.length === 0) return;
    const allNumbers = dateCombinations.map((c: any) => c.number).join("-");
    navigator.clipboard.writeText(allNumbers);
    setCopiedDateCombos(true);
    toast.success("Date combinations copied!");
    setTimeout(() => setCopiedDateCombos(false), 2000);
  };

  const handleSystemSelect = (system: SystemType) => {
    if (!isAuthenticated) {
      soundManager.playError();
      toast.error("Please login to use prediction systems");
      return;
    }
    soundManager.playClick();
    setSelectedSystem(system);
    setPredictions([]);
    setPredictionId(null);
    setDateCombinations(null);
  };

  const handleGenerate = () => {
    if (!selectedSystem) {
      toast.error("Please select a prediction system");
      return;
    }

    if (selectedSystem === "by_date" && !dateInput) {
      toast.error("Please select a date");
      return;
    }

    if (selectedSystem === "by_number" && !numberInput) {
      toast.error("Please enter a number");
      return;
    }

    soundManager.playPrediction();
    generateMutation.mutate({
      systemType: selectedSystem,
      digitType,
      count,
      dateInput: selectedSystem === "by_date" ? dateInput : undefined,
      numberInput: selectedSystem === "by_number" ? numberInput : undefined,
    });
  };

  const handleSaveForAnalysis = () => {
    if (!predictionId) {
      toast.error("No predictions to save");
      return;
    }

    soundManager.playClick();
    saveForAnalysisMutation.mutate({ predictionId });
  };

  const handleTonightsBest = () => {
    soundManager.playPrediction();
    tonightsBestMutation.mutate();
  };

  // Share Dialog Component (inline)
  const ShareDialog = ({
    nums,
    dateStr,
    onClose,
    copied,
    onCopy,
  }: {
    nums: any[];
    dateStr: string;
    onClose: () => void;
    copied: boolean;
    onCopy: () => void;
  }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <Share2 className="w-5 h-5 text-blue-400" />
            Share Predictions
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Preview */}
        <div className="bg-slate-800 rounded-lg p-4 mb-5 border border-slate-600">
          <p className="text-gray-300 text-sm whitespace-pre-line leading-relaxed">
            {buildShareText(nums, dateStr)}
          </p>
        </div>

        {/* Share Options */}
        <div className="grid grid-cols-1 gap-3">
          {/* WhatsApp */}
          <button
            onClick={() => handleWhatsApp(nums, dateStr)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-green-600/20 border border-green-500/40 hover:bg-green-600/30 text-green-400 font-semibold transition-all"
          >
            <MessageCircle className="w-5 h-5" />
            Share via WhatsApp
          </button>

          {/* SMS */}
          <button
            onClick={() => handleSMS(nums, dateStr)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-blue-600/20 border border-blue-500/40 hover:bg-blue-600/30 text-blue-400 font-semibold transition-all"
          >
            <Phone className="w-5 h-5" />
            Share via SMS
          </button>

          {/* Copy Text */}
          <button
            onClick={onCopy}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all ${
              copied
                ? "bg-green-500/20 border border-green-500/40 text-green-400"
                : "bg-slate-700/50 border border-slate-600 hover:bg-slate-700 text-gray-300"
            }`}
          >
            {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            {copied ? "Copied!" : "Copy Text"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      <Navigation />

      {/* Share Dialog for regular predictions */}
      {showShareDialog && predictions.length > 0 && (
        <ShareDialog
          nums={predictions}
          dateStr={new Date().toISOString().split("T")[0]}
          onClose={() => setShowShareDialog(false)}
          copied={shareCopied}
          onCopy={() => handleShareCopy(predictions, new Date().toISOString().split("T")[0])}
        />
      )}

      {/* Share Dialog for Tonight's Best */}
      {showTonightShareDialog && tonightPredictions.length > 0 && (
        <ShareDialog
          nums={tonightPredictions}
          dateStr={tonightDate}
          onClose={() => setShowTonightShareDialog(false)}
          copied={tonightShareCopied}
          onCopy={handleTonightShareCopy}
        />
      )}

      <div className="container mx-auto px-4 py-8 pb-24 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-blue-400">AI </span>
            <span className="text-yellow-400">Prediction</span>
            <span className="text-gray-300"> Generator</span>
          </h1>
          <p className="text-gray-400 text-lg">
            Generate intelligent lottery predictions using advanced AI analysis
          </p>
        </div>

        {/* ⭐ Tonight's Best Numbers — Prominent CTA */}
        <div className="mb-8">
          <div className="relative rounded-2xl overflow-hidden border border-yellow-500/40 bg-gradient-to-br from-yellow-900/30 via-amber-900/20 to-slate-900/60 p-6 md:p-8">
            {/* Decorative stars */}
            <div className="absolute top-3 right-4 text-yellow-500/20 text-5xl select-none pointer-events-none">✦</div>
            <div className="absolute bottom-3 left-4 text-yellow-500/10 text-3xl select-none pointer-events-none">✦</div>

            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                  <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                  <h2 className="text-2xl md:text-3xl font-bold text-yellow-400">Tonight's Best Numbers</h2>
                  <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                </div>
                <p className="text-gray-300 text-sm md:text-base">
                  All 4 AI engines (Math, Numerology, Astrology, Statistics) optimized for{" "}
                  <span className="text-yellow-300 font-semibold">tonight's 9 PM draw</span>.
                  Top 5 numbers selected.
                </p>
              </div>
              <Button
                onClick={handleTonightsBest}
                disabled={tonightsBestMutation.isPending}
                className="shrink-0 bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 text-slate-950 font-bold text-lg px-8 py-6 rounded-xl shadow-lg shadow-yellow-500/20"
              >
                {tonightsBestMutation.isPending ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Star className="w-5 h-5 mr-2 fill-current" />
                    Get Tonight's Numbers
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Tonight's Best Results */}
        {showTonightResults && tonightPredictions.length > 0 && (
          <div className="mb-10">
            <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
              <h2 className="text-2xl font-bold text-yellow-400 flex items-center gap-2">
                <Star className="w-6 h-6 fill-yellow-400" />
                Tonight's Best — {formatDate(tonightDate)}
              </h2>
              <div className="flex gap-2">
                {/* Copy All Tonight */}
                <button
                  onClick={() => {
                    const nums = tonightPredictions.map((p) => p.number).join("-");
                    navigator.clipboard.writeText(nums);
                    setTonightCopiedAll(true);
                    toast.success("Tonight's numbers copied!");
                    setTimeout(() => setTonightCopiedAll(false), 2000);
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    tonightCopiedAll
                      ? "bg-green-500/20 text-green-400 border border-green-500/40"
                      : "bg-yellow-500/20 text-yellow-300 border border-yellow-500/40 hover:bg-yellow-500/30"
                  }`}
                >
                  {tonightCopiedAll ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {tonightCopiedAll ? "Copied!" : "Copy All"}
                </button>
                {/* Share Tonight */}
                <button
                  onClick={() => setShowTonightShareDialog(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-green-500/20 text-green-300 border border-green-500/40 hover:bg-green-500/30 transition-all"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tonightPredictions.map((prediction, index) => (
                <PredictionCard key={`tonight-${index}`} prediction={prediction} rank={index + 1} />
              ))}
            </div>
          </div>
        )}

        {/* Choose System Section */}
        <Card className="p-6 mb-8 bg-slate-900/50 border-slate-700">
          <h2 className="text-2xl font-bold text-blue-400 mb-4 flex items-center gap-2">
            <Sparkles className="w-6 h-6" />
            Choose System
          </h2>
          <p className="text-gray-400 mb-6">Select your preferred prediction method</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Generate Freely */}
            <button
              onClick={() => handleSystemSelect("freely")}
              className={`relative p-6 rounded-lg border-2 transition-all ${
                selectedSystem === "freely"
                  ? "border-blue-500 bg-blue-500/10"
                  : "border-slate-700 bg-slate-800/50 hover:border-slate-600"
              } ${!isAuthenticated ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              {!isAuthenticated && (
                <div className="absolute top-2 right-2">
                  <Lock className="w-4 h-4 text-gray-500" />
                </div>
              )}
              <Sparkles className="w-12 h-12 text-blue-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Generate Freely</h3>
              <p className="text-sm text-gray-400">
                AI analyzes entire database to find optimal patterns
              </p>
            </button>

            {/* Pick by Date */}
            <button
              onClick={() => handleSystemSelect("by_date")}
              className={`relative p-6 rounded-lg border-2 transition-all ${
                selectedSystem === "by_date"
                  ? "border-blue-500 bg-blue-500/10"
                  : "border-slate-700 bg-slate-800/50 hover:border-slate-600"
              } ${!isAuthenticated ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              {!isAuthenticated && (
                <div className="absolute top-2 right-2">
                  <Lock className="w-4 h-4 text-gray-500" />
                </div>
              )}
              <Calendar className="w-12 h-12 text-blue-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Pick by Date</h3>
              <p className="text-sm text-gray-400">
                Generate based on zodiac, numerology & date analysis
              </p>
            </button>

            {/* Pick a Number */}
            <button
              onClick={() => handleSystemSelect("by_number")}
              className={`relative p-6 rounded-lg border-2 transition-all ${
                selectedSystem === "by_number"
                  ? "border-blue-500 bg-blue-500/10"
                  : "border-slate-700 bg-slate-800/50 hover:border-slate-600"
              } ${!isAuthenticated ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              {!isAuthenticated && (
                <div className="absolute top-2 right-2">
                  <Lock className="w-4 h-4 text-gray-500" />
                </div>
              )}
              <Hash className="w-12 h-12 text-blue-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Pick a Number</h3>
              <p className="text-sm text-gray-400">
                Extract all combinations from your lucky number
              </p>
            </button>
          </div>
        </Card>

        {/* Configuration Section */}
        {selectedSystem && (
          <Card className="p-6 mb-8 bg-gradient-to-br from-slate-900 to-slate-800 border-yellow-500/30">
            <h2 className="text-2xl font-bold text-yellow-400 mb-6">Prediction Settings</h2>

            {/* Number Type */}
            <div className="mb-6">
              <Label className="text-gray-300 mb-3 block">Number Type</Label>
              <RadioGroup
                value={digitType.toString()}
                onValueChange={(value) => {
                  soundManager.playClick();
                  setDigitType(parseInt(value));
                }}
                className="flex gap-4"
              >
                {[2, 3, 4].map((type) => (
                  <div key={type} className="flex items-center space-x-2">
                    <RadioGroupItem value={type.toString()} id={`digit-${type}`} />
                    <Label htmlFor={`digit-${type}`} className="text-white cursor-pointer">
                      {type} Digits
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Number of Predictions Slider */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <Label className="text-gray-300">Number of Predictions</Label>
                <span className="text-2xl font-bold text-yellow-400">{count}</span>
              </div>
              <Slider
                value={[count]}
                onValueChange={(value) => {
                  soundManager.playClick();
                  setCount(value[0]);
                }}
                min={1}
                max={50}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>1</span>
                <span>25</span>
                <span>50</span>
              </div>
            </div>

            {/* Date Input (for by_date system) */}
            {selectedSystem === "by_date" && (
              <div className="mb-6">
                <Label htmlFor="date-input" className="text-gray-300 mb-2 block">
                  Select Date
                </Label>
                <Input
                  id="date-input"
                  type="date"
                  value={dateInput}
                  onChange={(e) => {
                    soundManager.playClick();
                    setDateInput(e.target.value);
                  }}
                  className="bg-slate-800 border-slate-700 text-white"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Enter any meaningful date: birthday, anniversary, lucky day. The AI will generate every possible combination from this date and rank them by historical frequency.
                </p>
              </div>
            )}

            {/* Number Input (for by_number system) */}
            {selectedSystem === "by_number" && (
              <div className="mb-6">
                <Label htmlFor="number-input" className="text-gray-300 mb-2 block">
                  Enter Your Number
                </Label>
                <Input
                  id="number-input"
                  type="text"
                  value={numberInput}
                  onChange={(e) => {
                    soundManager.playClick();
                    setNumberInput(e.target.value.replace(/\D/g, ""));
                  }}
                  placeholder="Enter any number (e.g., phone number, ID, lucky number)"
                  className="bg-slate-800 border-slate-700 text-white"
                />
                <p className="text-xs text-gray-500 mt-2">
                  AI will extract all possible {digitType}-digit combinations from your number
                </p>
              </div>
            )}

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={generateMutation.isPending}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-lg py-6"
            >
              {generateMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating Predictions...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate Predictions
                </>
              )}
            </Button>
          </Card>
        )}

        {/* Predictions Results */}
        {predictions.length > 0 && (
          <div>
            {/* Save for Analysis Button */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold text-yellow-400">
                Your Predictions ({predictions.length})
              </h2>
              <Button
                onClick={handleSaveForAnalysis}
                disabled={saveForAnalysisMutation.isPending}
                variant="outline"
                className="border-yellow-500 text-yellow-500 hover:bg-yellow-500/10"
              >
                {saveForAnalysisMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save for Analysis
                  </>
                )}
              </Button>
            </div>

            {/* Copy All + Share Buttons */}
            <div className="mb-4 flex justify-end gap-2">
              <button
                onClick={handleCopyAll}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  copiedAll
                    ? "bg-green-500/20 text-green-400 border border-green-500/40"
                    : "bg-blue-500/20 text-blue-300 border border-blue-500/40 hover:bg-blue-500/30"
                }`}
              >
                {copiedAll ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copiedAll ? "Copied All!" : "Copy All Predictions"}
              </button>
              <button
                onClick={() => setShowShareDialog(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-green-500/20 text-green-300 border border-green-500/40 hover:bg-green-500/30 transition-all"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>

              {/* Date Combinations Panel — shown for Pick by Date, uses dedicated dateCombinations from backend */}
            {selectedSystem === "by_date" && dateCombinations && dateCombinations.length > 0 && (
              <div className="mb-8 p-5 rounded-xl bg-gradient-to-br from-blue-950/60 to-slate-900/80 border border-blue-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-5 h-5 text-blue-400" />
                  <h3 className="text-lg font-bold text-blue-300">Your Date Combinations</h3>
                  <span className="text-xs text-gray-500 ml-auto">{dateCombinations.length} numbers from your date</span>
                </div>
                {/* Prominent Copy All button — same style as Copy All Predictions */}
                <div className="mb-4 flex justify-end">
                  <button
                    onClick={handleCopyDateCombinations}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      copiedDateCombos
                        ? "bg-green-500/20 text-green-400 border border-green-500/40"
                        : "bg-blue-500/20 text-blue-300 border border-blue-500/40 hover:bg-blue-500/30"
                    }`}
                  >
                    {copiedDateCombos ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copiedDateCombos ? "Copied!" : "Copy All Date Combinations"}
                  </button>
                </div>
                {/* Group by combination type */}
                {(() => {
                  const groupOrder = ['Direct Date', 'Age-Based', 'Arithmetic', 'Mirror', 'Other'];
                  const groups: Record<string, any[]> = { 'Direct Date': [], 'Age-Based': [], 'Arithmetic': [], 'Mirror': [], 'Other': [] };
                  dateCombinations.forEach((c: any) => {
                    if (c.group === 'age') groups['Age-Based'].push(c);
                    else if (c.group === 'mirror') groups['Mirror'].push(c);
                    else if (c.group === 'arithmetic') groups['Arithmetic'].push(c);
                    else if (c.group === 'direct') groups['Direct Date'].push(c);
                    else groups['Other'].push(c);
                  });
                  return groupOrder.filter(g => groups[g].length > 0).map(groupName => (
                    <div key={groupName} className="mb-4">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{groupName}</p>
                      <div className="flex flex-wrap gap-2">
                        {groups[groupName].map((c: any, i: number) => (
                          <div key={i} className="flex flex-col items-center bg-slate-800/70 border border-blue-500/20 rounded-lg px-3 py-2 min-w-[64px]">
                            <span className="text-xl font-bold text-white tracking-widest">{c.number}</span>
                            <span className="text-[10px] text-blue-400 mt-0.5">{c.label}</span>
                            {c.frequency > 0 ? (
                              <span className="text-[10px] text-green-400">{c.frequency}x</span>
                            ) : (
                              <span className="text-[10px] text-gray-600">new</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}

            {/* Prediction Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {predictions.map((prediction, index) => (
                <PredictionCard key={index} prediction={prediction} rank={index + 1} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
